// Placeholder for MessageThread component
// This will display the conversation thread for a selected contact

import React, { useMemo } from 'react';
import { getUnifiedTimestamp } from '../utils/combinedMessageUtils';

// Utility functions moved outside component to avoid hoisting issues
const getInitials = (name) => {
  if (!name || name === 'Unknown') return '?';

  if (name.includes(' ')) {
    const words = name.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length === 2 && words[1].match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/)) {
      // Treat as single word with trailing emoji
      const fullName = words.join('');
      const emojiRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
      const emojiMatch = fullName.match(emojiRegex);
      const emoji = emojiMatch ? emojiMatch[0] : '';
      const textWithoutEmoji = fullName.replace(emojiRegex, '').trim();
      const firstLetter = textWithoutEmoji.length > 0 ? textWithoutEmoji.charAt(0).toUpperCase() : '';
      if (!firstLetter && emoji) return emoji;
      if (!emoji) return textWithoutEmoji.substring(0, 2).toUpperCase();
      return `${firstLetter}${emoji}`;
    } else if (words.length >= 2) {
      // Normal multi-word
      return words[0][0] + words[words.length - 1][0];
    }
  }

  // For single words without space
  const emojiRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]/;
  const emojiMatch = name.match(emojiRegex);
  const emoji = emojiMatch ? emojiMatch[0] : '';
  const textWithoutEmoji = name.replace(emojiRegex, '').trim();
  const firstLetter = textWithoutEmoji.length > 0 ? textWithoutEmoji.charAt(0).toUpperCase() : '';

  if (!firstLetter && emoji) return emoji;
  if (!emoji) return textWithoutEmoji.substring(0, 2).toUpperCase();
  return `${firstLetter}${emoji}`;
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';

  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const formatWhatsAppText = (text) => {
  if (!text || typeof text !== 'string') return text;

  let formatted = text;

  formatted = formatted.replace(/^(\s*)>\s+(.+)$/gm, '$1<blockquote>$2</blockquote>');
  formatted = formatted.replace(/^(\s*)>(.+)$/gm, '$1<blockquote>$2</blockquote>');

  formatted = formatted.replace(/^(\s*)-\s+(.+)$/gm, '$1• $2');
  formatted = formatted.replace(/^(\s*)-(.+)$/gm, '$1• $2');

  formatted = formatted.replace(/\*([^*\r\n]+?)\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/_([^_\r\n]+?)_/g, '<em>$1</em>');
  formatted = formatted.replace(/```([^`]+?)```/g, '<code>$1</code>');
  formatted = formatted.replace(/~([^~\r\n]+?)~/g, '<del>$1</del>');

  formatted = formatted.replace(/\*_([^*_\r\n]+?)_\*/g, '<strong><em>$1</em></strong>');
  formatted = formatted.replace(/_\*([^*_\r\n]+?)\*_/g, '<em><strong>$1</strong></em>');

  formatted = formatted.replace(/\r\n/g, '<br>');
  formatted = formatted.replace(/\n/g, '<br>');
  formatted = formatted.replace(/\r/g, '<br>');

  return formatted;
};

const getMessageTypeDisplay = (type) => {
  const typeMap = {
    'text': '📝 Text',
    'button': '🔘 Button',
    'image': '🖼️ Image',
    'video': '🎥 Video',
    'audio': '🎵 Audio',
    'document': '📄 Document',
    'location': '📍 Location',
    'contact': '👤 Contact',
    'interactive': '🎯 Interactive',
    'template': '📋 Template',
    'media': '📎 Media',
    'WhatsApp Chat': '💬 WhatsApp',
    'SMS': '📱 SMS',
    'Email': '📧 Email',
    'Notification': '🔔 Notification',
    'System': '⚙️ System'
  };

  return typeMap[type] || '📤 Message';
};

const extractMessageInfo = (payload) => {
  if (!payload) return { name: 'Unknown', phone: 'Unknown', body: '', type: 'text' };

  const phone = extractPhoneNumber(payload.From || payload.from);
  const name = payload.ProfileName || phone || 'Unknown';

  let body = '';
  if (payload.Body) {
    body = payload.Body;
  } else if (payload.text) {
    body = payload.text;
  } else if (payload.message?.text) {
    body = payload.message.text;
  } else if (payload.message?.body) {
    body = payload.message.body;
  } else if (payload.ButtonText) {
    body = `🔘 ${payload.ButtonText}`;
  } else if (payload.ButtonPayload) {
    body = `🔘 Button: ${payload.ButtonPayload}`;
  }

  let type = payload.MessageType || payload.message?.type || 'text';
  if (payload.ButtonText || payload.ButtonPayload) {
    type = 'button';
  }

  // Check for interactive button replies
  if (payload.interactive && payload.interactive.type === 'button_reply') {
    type = 'button';
    body = payload.interactive.button_reply.title || `Button: ${payload.interactive.button_reply.id}`;
  }

  // Check for interactive list replies
  if (payload.interactive && payload.interactive.type === 'list_reply') {
    type = 'interactive';
    body = payload.interactive.list_reply.title || `List: ${payload.interactive.list_reply.id}`;
  }

  return { name, phone, body, type };
};

const extractPhoneNumber = (from) => {
  if (!from) return 'Unknown';
  const match = from.match(/(\+?\d{10,15})/);
  return match ? match[1] : from;
};

export const MessageThread = ({ selectedContact, contacts, onSendMessage, whatsappRole, filterButtonMessages }) => {
  if (!selectedContact || !Array.isArray(contacts) || !contacts.find(c => c.phone === selectedContact)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <span className="text-2xl">💬</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
          <p className="text-gray-600">Choose a conversation from the sidebar to view messages</p>
        </div>
      </div>
    );
  }

  const contact = contacts.find(c => c.phone === selectedContact);

  // Memoize sorted and filtered messages to prevent filtering on every render
  const messages = useMemo(() => {
    if (!contact || !Array.isArray(contact.messages)) return [];

    // First filter messages if button filter is active
    const filteredMessages = filterButtonMessages 
      ? contact.messages.filter(msg => msg.type !== 'button')
      : contact.messages;

    const sortedMessages = [...filteredMessages].sort((a, b) => {
      // Use unified timestamp for proper sorting of both incoming and outgoing
      const timeA = getUnifiedTimestamp(a.timestamp || a.unified_timestamp);
      const timeB = getUnifiedTimestamp(b.timestamp || b.unified_timestamp);
      return timeB - timeA; // Newest first
    });

    // Limit to 20 messages for performance
    const limitedMessages = sortedMessages.slice(0, 20);

    // Pre-format WhatsApp text for each message to avoid formatting on every render
    return limitedMessages.map(msg => {
      // Handle both incoming and outgoing message structures
      let messageInfo, content;
      
      if (msg.direction === 'outgoing') {
        // Outgoing message structure
        messageInfo = {
          name: msg.meta?.sender || 'PLSSS',
          phone: msg.phone,
          body: msg.body || '',
          type: msg.type || 'text',
          process_name: msg.meta?.process_name || ''
        };
        content = formatWhatsAppText(msg.body || '');
      } else {
        // Incoming message structure
        messageInfo = extractMessageInfo(msg.payload);
        content = formatWhatsAppText(messageInfo.body);
      }
      
      return {
        ...msg,
        messageInfo,
        formattedBody: content
      };
    });
  }, [contact.messages, filterButtonMessages]);

  const messageInfo = contact;
  const initials = getInitials(messageInfo.name);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold" style={{fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif'}}>
              {initials}
            </div>
            <div>
              <div className="font-semibold text-gray-900" style={{fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif'}}>{messageInfo.name}</div>
              <div className="text-sm text-gray-500 font-medium">{messageInfo.phone}</div>
            </div>
          </div>
          <button
            onClick={onSendMessage}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2"
          >
            <span>📤</span>
            <span>Send Message</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map(msg => {
          const messageInfo = msg.messageInfo || msg;
          const content = msg.formattedBody;
          const time = formatTime(msg.timestamp || msg.unified_timestamp);
          const dateVal = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp || msg.unified_timestamp);
          const fullTime = dateVal.toLocaleString();
          const typeDisplay = getMessageTypeDisplay(messageInfo.type);
          const isOutgoing = msg.direction === 'outgoing';

          return (
            <div key={msg.id} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-xs lg:max-w-md">
                <div className={`${isOutgoing ? 'bg-teal-600 text-white' : 'bg-white border border-gray-200'} rounded-lg shadow-sm`}>
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm ${isOutgoing ? 'text-teal-100' : 'text-gray-500'}`}>
                        {isOutgoing ? `📤 ${messageInfo.name}` : typeDisplay}
                      </span>
                      <span className={`text-sm ${isOutgoing ? 'text-teal-100' : 'text-gray-400'}`} title={fullTime}>{time}</span>
                    </div>
                    <div
                      className={`text-base whitespace-pre-wrap ${isOutgoing ? 'text-white' : 'text-gray-900'}`}
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  </div>
                  {['admin', 'manager'].includes(whatsappRole?.toLowerCase()) && !isOutgoing && (
                    <>
                      <div className={`${isOutgoing ? 'border-teal-500' : 'border-t border-gray-100'} p-2`}>
                        <button
                          onClick={() => {
                            const payloadDiv = document.getElementById(`payload-${msg.id}`);
                            payloadDiv.style.display = payloadDiv.style.display === 'block' ? 'none' : 'block';
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          Show Payload
                        </button>
                      </div>
                      <div
                        id={`payload-${msg.id}`}
                        className="hidden border-t border-gray-100"
                      >
                        <pre className="p-3 text-sm bg-gray-50 overflow-x-auto">
                          {JSON.stringify(msg.payload, null, 2)}
                        </pre>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MessageThread;
